import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import {
  createPatientReferral,
  createPatientReferralDraft,
  submitReferralDraft,
  uploadPatientReferralDocument,
  dispatchReferralFax,
} from "../APIs/referralApi";
import { getSpecialties } from "../APIs/dropdownApi";
import { searchProviders, getProviderByNpi } from "../APIs/providerApi";
import { getPatient, getPatientPreference } from "../APIs/patientService";

const URGENCY_OPTIONS = [
  { value: "ROUTINE", label: "Routine" },
  { value: "WITHIN_30_DAYS", label: "Within 30 Days" },
  { value: "WITHIN_7_DAYS", label: "Within 7 Days" },
  { value: "WITHIN_3_DAYS", label: "Within 3 Days" },
];

const REASON_CHIPS = [
  "Follow-up",
  "New Symptoms",
  "Second Opinion",
  "Abnormal Results",
  "Other",
];

const DELIVERY_OPTIONS = [
  { value: "IN_APP", label: "Send via MedMaze" },
  { value: "FAX", label: "Send via Fax" },
  { value: "BOTH", label: "Send via Both" },
];

const DOC_CATEGORIES = [
  { value: "REFERRAL_DOCUMENT", label: "Referral Document" },
  { value: "CLINICAL_NOTES", label: "Clinical Notes" },
  { value: "LAB_RESULTS", label: "Lab Results" },
  { value: "IMAGING", label: "Imaging" },
  { value: "INSURANCE_FRONT", label: "Insurance Front" },
  { value: "INSURANCE_BACK", label: "Insurance Back" },
];

const ALLOWED_DOC_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/tiff",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/csv",
  "application/csv",
]);

const MAX_DOCS = 5;
const MAX_DOC_BYTES = 50 * 1024 * 1024;
const STEP_TITLES = [
  "Find a Specialist",
  "Send Referral",
  "Clinical Details",
  "Delivery Method",
];

const providerName = (p) =>
  `${p?.firstName ?? ""} ${p?.lastName ?? ""}${p?.credential ? `, ${p.credential}` : ""}`.trim();

const providerSpecialty = (p) => {
  if (p?.specialty) return p.specialty;
  if (Array.isArray(p?.specialties) && p.specialties.length > 0) {
    return p.specialties
      .map((s) => s?.name ?? s)
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

const providerLocation = (p) => {
  const location = p?.locations?.[0] ?? p?.facilityLocations?.[0];
  if (!location) return "";
  return [location.city, location.state].filter(Boolean).join(", ");
};

export default function PatientReferralIntakeModal({
  patientId,
  onClose,
  onSubmitted,
}) {
  const [step, setStep] = useState(1);
  const [specialties, setSpecialties] = useState([]);
  const [patient, setPatient] = useState(null);
  const [specialistQuery, setSpecialistQuery] = useState("");
  const [referrerQuery, setReferrerQuery] = useState("");
  const [specialistResults, setSpecialistResults] = useState([]);
  const [referrerResults, setReferrerResults] = useState([]);
  const [searchingSpecialist, setSearchingSpecialist] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showRequiredHint, setShowRequiredHint] = useState(false);
  const [error, setError] = useState("");
  const [documents, setDocuments] = useState([]);
  const [draftReferralId, setDraftReferralId] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("IN_APP");
  const [faxNumber, setFaxNumber] = useState("");
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    targetProvider: null,
    referringProvider: null,
    specialty: "",
    urgency: "ROUTINE",
    reasonChips: [],
    additionalNotes: "",
    city: "",
    state: "",
    insurancePlan: "",
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const isExternal = form.targetProvider?._type === "external";
  const isFax = deliveryMethod === "FAX";
  const isBoth = deliveryMethod === "BOTH";
  const needsFax = isExternal || isFax || isBoth;
  const reason = [...form.reasonChips, form.additionalNotes.trim()]
    .filter(Boolean)
    .join(" | ");

  useEffect(() => {
    getSpecialties()
      .then((data) => setSpecialties(data || []))
      .catch(() => {});
    getPatient(patientId)
      .then((p) => {
        setPatient(p);
        setForm((f) => ({ ...f, city: p.city ?? "", state: p.state ?? "" }));
      })
      .catch(() => {});
    getPatientPreference(patientId)
      .then((prefs) => {
        if (!prefs?.planNames) return;
        const plan = Array.isArray(prefs.planNames)
          ? prefs.planNames.join(", ")
          : String(prefs.planNames);
        setForm((f) => ({ ...f, insurancePlan: plan }));
      })
      .catch(() => {});
  }, [patientId]);

  useEffect(() => {
    if (!form.targetProvider) return;
    if (form.targetProvider._type === "external") {
      setDeliveryMethod("FAX");
      setFaxNumber(form.targetProvider.faxNumber ?? "");
    } else {
      setDeliveryMethod("IN_APP");
      setFaxNumber("");
    }
  }, [form.targetProvider]);

  useEffect(() => {
    if (step !== 1 || specialistQuery.length < 2) {
      setSpecialistResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearchingSpecialist(true);
      const digits = specialistQuery.replace(/\D/g, "");
      try {
        if (digits.length === 10) {
          const preview = await getProviderByNpi(digits);
          setSpecialistResults([
            { ...preview, npi: digits, _type: "external" },
          ]);
        } else {
          const list = await searchProviders(specialistQuery, 10);
          setSpecialistResults(list.map((p) => ({ ...p, _type: "internal" })));
        }
      } catch {
        setSpecialistResults([]);
      } finally {
        setSearchingSpecialist(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [specialistQuery, step]);

  useEffect(() => {
    if (step !== 2 || referrerQuery.length < 2) {
      setReferrerResults([]);
      return;
    }
    const t = setTimeout(async () => {
      const digits = referrerQuery.replace(/\D/g, "");
      try {
        if (digits.length === 10) {
          const preview = await getProviderByNpi(digits);
          const isInternal = !!preview.providerId;
          setReferrerResults([
            {
              ...preview,
              npi: digits,
              _type: isInternal ? "internal" : "external",
            },
          ]);
        } else {
          const list = await searchProviders(referrerQuery, 10);
          setReferrerResults(list.map((p) => ({ ...p, _type: "internal" })));
        }
      } catch {
        setReferrerResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [referrerQuery, step]);

  const addFile = (file) => {
    if (draftReferralId) return;
    if (documents.length >= MAX_DOCS) return;
    const error = !ALLOWED_DOC_TYPES.has(file.type)
      ? "File type not supported"
      : file.size > MAX_DOC_BYTES
        ? "File size must not exceed 50MB"
        : "";
    setDocuments((d) => [...d, { file, category: "REFERRAL_DOCUMENT", error }]);
  };

  const handleFileInput = (e) => {
    Array.from(e.target.files).forEach(addFile);
    e.target.value = "";
  };

  const updateDoc = (idx, changes) =>
    setDocuments((d) =>
      d.map((doc, i) => (i === idx ? { ...doc, ...changes } : doc)),
    );
  const removeDoc = (idx) => {
    if (draftReferralId) return;
    setDocuments((d) => d.filter((_, i) => i !== idx));
  };

  const handleNext = () => {
    if (step === 2 && (!form.referringProvider || !form.specialty)) {
      setShowRequiredHint(true);
      return;
    }
    setShowRequiredHint(false);
    setStep((s) => Math.min(s + 1, 4));
  };

  const canReview =
    !!form.targetProvider &&
    !!form.referringProvider &&
    !!form.specialty &&
    (!needsFax || faxNumber.trim().length > 0) &&
    documents.every((d) => !d.error);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...(form.referringProvider._type === "external"
          ? { externalReferringProviderNpi: form.referringProvider.npi }
          : {
              referringProviderId:
                form.referringProvider.providerId ?? form.referringProvider.id,
            }),
        routingMode: isExternal || isFax ? "EXTERNAL" : "PROVIDER_DIRECTED",
        deliveryMethod,
        specialty: form.specialty,
        urgency: form.urgency,
        reason,
        ...(form.state ? { state: form.state } : {}),
        ...(form.city ? { city: form.city } : {}),
        ...(form.insurancePlan ? { insurancePlan: form.insurancePlan } : {}),
        ...(!isExternal && !isFax
          ? {
              referredSpecialistProviderId:
                form.targetProvider.providerId ?? form.targetProvider.id,
            }
          : {}),
        ...((isExternal || isFax) && form.targetProvider.npi
          ? { externalProviderNpi: form.targetProvider.npi }
          : {}),
        ...(needsFax ? { externalProviderFaxOverride: faxNumber.trim() } : {}),
      };
      if (!needsFax) {
        let referralId = draftReferralId;
        if (!referralId) {
          const draft = await createPatientReferralDraft({
            ...payload,
            expectedDocumentCount: documents.length,
          });
          referralId = draft.referralId;
          setDraftReferralId(referralId);
        }
        for (let index = 0; index < documents.length; index += 1) {
          const doc = documents[index];
          if (doc.file && !doc.error && !doc.uploaded) {
            await uploadPatientReferralDocument(
              referralId,
              doc.file,
              doc.category,
            );
            updateDoc(index, { uploaded: true });
          }
        }
        await submitReferralDraft(referralId);
      } else {
        const referral = await createPatientReferral(payload);
        for (const doc of documents) {
          if (doc.file && !doc.error) {
            await uploadPatientReferralDocument(
              referral.referralId,
              doc.file,
              doc.category,
            );
          }
        }
        if (isExternal || isFax) {
          await dispatchReferralFax(referral.referralId);
        }
      }
      onSubmitted?.();
      onClose();
    } catch (err) {
      const fields = err?.response?.data?.fields;
      setError(
        (fields && Object.entries(fields).map(([field, message]) => `${field}: ${message}`).join("; ")) ||
          err?.response?.data?.message ||
          err.message ||
          "Submission failed",
      );
      setShowReview(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b">
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                Step {step} of 4
              </p>
              <h2 className="text-lg font-semibold">{STEP_TITLES[step - 1]}</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>

          <div className="h-1 bg-gray-100">
            <div
              className="h-1 bg-green-500 transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>

          <div className="p-5 space-y-5">
            {step === 1 && (
              <ProviderSearchStep
                text="Search by name or enter a 10-digit NPI to find a specialist."
                selected={form.targetProvider}
                query={specialistQuery}
                setQuery={setSpecialistQuery}
                results={specialistResults}
                searching={searchingSpecialist}
                onSelect={(p) => {
                  set("targetProvider", p);
                  setSpecialistQuery("");
                  setSpecialistResults([]);
                }}
                onClear={() => set("targetProvider", null)}
                allowExternal
              />
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Patient
                  </label>
                  <div className="border rounded-lg p-3 bg-green-50 text-sm font-medium">
                    {patient
                      ? `${patient.firstName} ${patient.lastName}`
                      : "Loading patient..."}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Referring Provider <span className="text-red-500">*</span>
                  </label>
                  <ProviderSearchStep
                    selected={form.referringProvider}
                    query={referrerQuery}
                    setQuery={setReferrerQuery}
                    results={referrerResults}
                    searching={false}
                    onSelect={(p) => {
                      set("referringProvider", p);
                      setReferrerQuery("");
                      setReferrerResults([]);
                    }}
                    onClear={() => set("referringProvider", null)}
                    compact
                  />
                  {showRequiredHint && !form.referringProvider && (
                    <p className="text-xs text-red-500 mt-1">
                      Required field - please complete
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Specialty <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 ${
                      showRequiredHint && !form.specialty
                        ? "border-red-400"
                        : ""
                    }`}
                    value={form.specialty}
                    onChange={(e) => set("specialty", e.target.value)}
                  >
                    <option value="">Select specialty...</option>
                    {specialties.map((s) => (
                      <option key={s.id ?? s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    How soon do you need care?
                  </label>
                  <div className="space-y-2">
                    {URGENCY_OPTIONS.map((u) => (
                      <label
                        key={u.value}
                        className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                          form.urgency === u.value
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          className="accent-green-600"
                          checked={form.urgency === u.value}
                          onChange={() => set("urgency", u.value)}
                        />
                        <span className="text-sm font-medium">{u.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Reason for Referral{" "}
                    <span className="text-gray-400 font-normal text-xs">
                      (optional)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {REASON_CHIPS.map((chip) => {
                      const active = form.reasonChips.includes(chip);
                      return (
                        <button
                          type="button"
                          key={chip}
                          onClick={() =>
                            set(
                              "reasonChips",
                              active
                                ? form.reasonChips.filter((c) => c !== chip)
                                : [...form.reasonChips, chip],
                            )
                          }
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                            active
                              ? "bg-green-600 text-white border-green-600"
                              : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          {chip}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none mt-3"
                    rows={3}
                    placeholder="Describe the referral reason..."
                    value={form.additionalNotes}
                    onChange={(e) => set("additionalNotes", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Upload Supporting Documents{" "}
                    <span className="text-gray-400 font-normal text-xs">
                      (optional)
                    </span>
                  </label>
                  <p className="text-xs text-amber-600 mb-2">
                    Maximum {MAX_DOCS} documents per referral.
                  </p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed rounded-lg p-5 text-center text-sm text-gray-600 hover:border-gray-400 bg-gray-50"
                  >
                    Browse files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.docx,.doc,.csv"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                  {documents.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {documents.map((doc, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white">
                            <span className="flex-1 text-xs text-gray-700 truncate">
                              {doc.file.name}
                            </span>
                            <select
                              className="border-0 text-xs text-gray-600 focus:outline-none bg-transparent"
                              value={doc.category}
                              onChange={(e) =>
                                updateDoc(idx, { category: e.target.value })
                              }
                            >
                              {DOC_CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => removeDoc(idx)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          {doc.error && (
                            <p className="text-xs text-red-600 px-1">
                              {doc.error}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    How would you like to send this referral?
                  </label>
                  {isExternal ? (
                    <div className="flex items-center gap-2 border rounded-lg px-4 py-3 bg-gray-50">
                      <span className="text-sm font-medium text-gray-700">
                        Send via Fax
                      </span>
                      <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                        Locked - external provider
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {DELIVERY_OPTIONS.map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                            deliveryMethod === opt.value
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            className="accent-green-600"
                            checked={deliveryMethod === opt.value}
                            onChange={() => {
                              setDeliveryMethod(opt.value);
                              if (opt.value === "IN_APP") setFaxNumber("");
                            }}
                          />
                          <span className="text-sm font-medium">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {needsFax && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Fax Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                      placeholder="(555) 555-5555"
                      value={faxNumber}
                      onChange={(e) => setFaxNumber(e.target.value)}
                    />
                  </div>
                )}
                {error && <p className="text-red-600 text-sm">{error}</p>}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50 sticky bottom-0">
            <button
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(s - 1, 1))}
              className="px-4 py-2 text-sm rounded-lg border disabled:opacity-40 hover:bg-gray-100 transition"
            >
              Back
            </button>
            {step < 4 ? (
              <button
                disabled={step === 1 && !form.targetProvider}
                onClick={handleNext}
                className="px-5 py-2 text-sm rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition"
              >
                Next
              </button>
            ) : (
              <button
                disabled={!canReview}
                onClick={() => setShowReview(true)}
                className="px-5 py-2 text-sm rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition"
              >
                Review
              </button>
            )}
          </div>
        </div>
      </div>

      {showReview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">Review Referral</h2>
              <button
                onClick={() => setShowReview(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-2.5 text-sm">
              <ReviewRow
                label="Specialist"
                value={`${providerName(form.targetProvider)} (${isExternal ? "External" : "MedMaze"})`}
              />
              <ReviewRow
                label="Patient"
                value={
                  patient ? `${patient.firstName} ${patient.lastName}` : "-"
                }
              />
              <ReviewRow
                label="Referrer"
                value={providerName(form.referringProvider)}
              />
              <ReviewRow label="Specialty" value={form.specialty} />
              <ReviewRow
                label="Urgency"
                value={
                  URGENCY_OPTIONS.find((u) => u.value === form.urgency)?.label
                }
              />
              <ReviewRow label="Reason" value={reason} />
              {needsFax && <ReviewRow label="Fax" value={faxNumber} />}
              <ReviewRow
                label="Documents"
                value={`${documents.filter((d) => d.file && !d.error).length}`}
              />
            </div>
            {error && <p className="px-5 pb-2 text-red-600 text-sm">{error}</p>}
            <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowReview(false)}
                className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-100 transition"
              >
                Back
              </button>
              <button
                disabled={submitting}
                onClick={handleSubmit}
                className="px-5 py-2 text-sm rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition"
              >
                {submitting ? "Submitting..." : "Submit Referral"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProviderSearchStep({
  text,
  selected,
  query,
  setQuery,
  results,
  searching,
  onSelect,
  onClear,
  compact = false,
}) {
  if (selected) {
    return (
      <div className="border rounded-lg p-3 bg-green-50 space-y-1">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-semibold">{providerName(selected)}</p>
            {providerSpecialty(selected) && (
              <p className="text-xs text-gray-500">
                {providerSpecialty(selected)}
              </p>
            )}
            {providerLocation(selected) && (
              <p className="text-xs text-gray-400">
                {providerLocation(selected)}
              </p>
            )}
          </div>
          <button
            onClick={onClear}
            className="text-xs text-red-500 hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {text && <p className="text-sm text-gray-500">{text}</p>}
      <input
        className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
        placeholder={
          compact
            ? "Search by name or NPI..."
            : "Search by name or enter NPI..."
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus={!compact}
      />
      {searching && <p className="text-xs text-gray-400">Searching...</p>}
      {!searching && results.length > 0 && (
        <div className="border rounded-lg mt-1 max-h-60 overflow-y-auto shadow-sm">
          {results.map((p, i) => (
            <button
              key={p.providerId ?? p.npi ?? i}
              className="w-full text-left px-3 py-3 text-sm hover:bg-blue-50 border-b last:border-b-0 transition"
              onClick={() => onSelect(p)}
            >
              <p className="font-medium">{providerName(p)}</p>
              {providerSpecialty(p) && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {providerSpecialty(p)}
                </p>
              )}
              {providerLocation(p) && (
                <p className="text-xs text-gray-400">
                  {providerLocation(p)}
                  {p.faxNumber && (
                    <span className="ml-2">Fax: {p.faxNumber}</span>
                  )}
                </p>
              )}
            </button>
          ))}
        </div>
      )}
      {!searching && query.length >= 2 && results.length === 0 && (
        <p className="text-xs text-gray-400">No providers found.</p>
      )}
    </>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="font-medium text-gray-500 w-24 shrink-0">{label}:</span>
      <span className="text-gray-800">{value || "-"}</span>
    </div>
  );
}
